import { NoSsr } from "@mui/base";
import { notEmpty } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, useMemo } from "react";

import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { HeaderTab } from "~/components/tracking/google-analytics-4/types";
import { config } from "~/data";
import { rosettaAtom } from "~/lib/rosetta";
import { pathname as homePathName } from "~/pages/home";
import { pathname as plusPathName } from "~/pages/plus";
import { pathname as plusLearnMorePathName } from "~/pages/plus/learnmore";

import { Container, SCMPPlusTabsList } from "./styles";

type Props = {
  className?: string;
  isPlus: boolean;
};

export const SCMPPlusTabs: FunctionComponent<Props> = ({ className, isPlus }) => {
  const router = useRouter();
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const hasSCMPPlusAccess = asyncRosettaState?.result?.user?.hasSCMPPlusAccessRight ?? false;
  const plusTrialPath = `${config.rosetta.subscribeHost}/plus`;

  const plusTabPath = useMemo(() => {
    if (plusLearnMorePathName === router.pathname) return plusLearnMorePathName;

    if (!notEmpty(asyncRosettaState) || asyncRosettaState?.status === "loading") return "";
    if (!hasSCMPPlusAccess) return plusTrialPath;
    return plusPathName;
  }, [asyncRosettaState, hasSCMPPlusAccess, plusTrialPath, router.pathname]);

  const getTabClickHandler = (headerTab: HeaderTab) => () => {
    sendGA4Tracking({
      action: "click",
      category: "header",
      customized_parameters: {
        header: headerTab,
      },
      subcategory: "",
    });
  };

  return (
    <Container className={className}>
      <NoSsr>
        <SCMPPlusTabsList
          $isActive={!isPlus}
          $isPlus={isPlus}
          pathname={homePathName}
          onClick={getTabClickHandler("scmp")}
        >
          SCMP.COM
        </SCMPPlusTabsList>
        {plusTabPath && (
          <SCMPPlusTabsList
            $isActive={isPlus}
            $isPlus={isPlus}
            pathname={plusTabPath}
            onClick={getTabClickHandler("scmp_plus")}
          >
            SCMP <i>PLUS</i>
          </SCMPPlusTabsList>
        )}
      </NoSsr>
    </Container>
  );
};

SCMPPlusTabs.displayName = "SCMPPlusTabs";
